// Version history for a saved guide: list published snapshots, inspect a
// per-field diff against the current form state, load a snapshot into the
// editor, or roll back (forward-publish as a new version).

import { useCallback, useEffect, useState } from "react";
import type { CharacterProfile } from "../../types/guide";
import type { GuideVersion, GuideVersionSummary } from "../../services/versions";
import {
  getGuideVersion,
  listGuideVersions,
  rollbackGuideVersion,
} from "../../services/versions";
import { formatDateTime } from "../analytics/format";

// Fields worth diffing in the history view (the editable prompt/config
// surface — mirrors the backend's snapshot config, minus server bookkeeping).
const DIFF_FIELDS: (keyof CharacterProfile)[] = [
  "name", "role", "category", "tagline", "description",
  "personaOverlay", "knowledgePack", "boundaries", "greetingVariants",
  "responseStyle", "speechStyle", "tts",
  "speciality", "profession", "voiceStyle", "experienceIn", "howItWorks", "bestFor",
  "domains", "handoverEligible",
];

function fmt(value: unknown): string {
  if (value === null || value === undefined || value === "") return "(empty)";
  if (typeof value === "string") return value;
  return JSON.stringify(value, null, 1);
}

// Key-order-stable equality: snapshots round-trip through Postgres JSONB,
// which sorts object keys, so a naive stringify compare false-positives on
// every nested object.
export function sameValue(a: unknown, b: unknown): boolean {
  const stable = (v: unknown): string =>
    JSON.stringify(v, (_key, val) =>
      val && typeof val === "object" && !Array.isArray(val)
        ? Object.fromEntries(
            Object.entries(val as Record<string, unknown>).sort(([x], [y]) =>
              x.localeCompare(y),
            ),
          )
        : val,
    );
  return stable(a) === stable(b);
}

interface Props {
  guideId: string;
  currentVersion: number;
  current: CharacterProfile;
  // Fired after a rollback persisted a new live profile, or when the user
  // loads a snapshot into the editor form (unsaved).
  onRestored: (profile: CharacterProfile, message: string) => void;
}

export function VersionHistory({ guideId, currentVersion, current, onRestored }: Props) {
  const [versions, setVersions] = useState<GuideVersionSummary[]>([]);
  const [selected, setSelected] = useState<GuideVersion | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const reload = useCallback(() => {
    listGuideVersions(guideId)
      .then((r) => setVersions(r.items))
      .catch((e) => setError(`History unavailable: ${e.message}`));
  }, [guideId]);

  // currentVersion is a dependency so a publish/rollback done while this tab
  // is mounted refreshes the list immediately.
  useEffect(() => {
    setSelected(null);
    reload();
  }, [reload, currentVersion]);

  async function select(version: number) {
    setError("");
    try {
      setSelected(await getGuideVersion(guideId, version));
    } catch (e) {
      setError(`Failed to load v${version}: ${e instanceof Error ? e.message : e}`);
    }
  }

  function loadIntoEditor(snapshot: GuideVersion) {
    // Mirrors CharacterProfileService._profile_from_snapshot: lifecycle and
    // server-owned fields stay with the live row, and fields a snapshot
    // predates (voicePreviewUrl in 0008 backfills) must not be blanked by
    // the model default once the user hits Save.
    const restored: CharacterProfile = {
      ...snapshot.config,
      id: current.id,
      version: current.version,
      status: current.status,
      enabled: current.enabled,
      publishedAt: current.publishedAt,
      createdAt: current.createdAt,
      updatedAt: current.updatedAt,
      voicePreviewUrl: snapshot.config.voicePreviewUrl ?? current.voicePreviewUrl,
    };
    onRestored(restored, `Loaded v${snapshot.version} into the editor (not saved)`);
  }

  async function rollback(snapshot: GuideVersion) {
    const ok = confirm(
      `Roll back to v${snapshot.version}? This restores its config and publishes it ` +
        `as v${currentVersion + 1}. Unsaved edits in the form will be replaced.`,
    );
    if (!ok) return;
    setBusy(true);
    setError("");
    try {
      const updated = await rollbackGuideVersion(guideId, snapshot.version);
      onRestored(updated, `Rolled back to v${snapshot.version} → published as v${updated.version} ✓`);
      setSelected(null);
      reload();
    } catch (e) {
      setError(`Rollback failed: ${e instanceof Error ? e.message : e}`);
    } finally {
      setBusy(false);
    }
  }

  const diffs = selected
    ? DIFF_FIELDS.map((field) => {
        const prev = selected.config[field];
        const next = current[field];
        return { field, prev, next, changed: !sameValue(prev, next) };
      }).filter((d) => d.changed)
    : [];

  return (
    <div className="version-history">
      {error && <div className="callout">{error}</div>}
      {versions.length === 0 && !error && (
        <div className="state-box">
          No versions yet — publishing this companion mints v1.
        </div>
      )}

      <div className="version-list">
        {versions.map((v) => (
          <button
            key={v.version}
            className={`version-row${selected?.version === v.version ? " active" : ""}`}
            onClick={() => select(v.version)}
          >
            <span className="version-badge">v{v.version}</span>
            <span className="version-main">
              <span className="version-note">{v.note || "published"}</span>
              <span className="version-meta">
                {formatDateTime(v.createdAt)}
                {v.createdBy ? ` · ${v.createdBy}` : ""}
              </span>
            </span>
            {v.version === currentVersion && <span className="badge badge-published">live</span>}
          </button>
        ))}
      </div>

      {selected && (
        <div className="version-detail">
          <div className="panel-head">
            v{selected.version} vs current form
            <span className="spacer" />
            <button className="btn btn-sm" onClick={() => loadIntoEditor(selected)} disabled={busy}>
              Load into editor
            </button>
            <button
              className="btn btn-sm btn-primary"
              onClick={() => rollback(selected)}
              disabled={busy || selected.version === currentVersion}
            >
              {busy ? "…" : `Roll back to v${selected.version}`}
            </button>
          </div>
          {diffs.length === 0 ? (
            <div className="muted" style={{ fontSize: 12.5 }}>
              Identical to the current form on all tracked fields.
            </div>
          ) : (
            diffs.map(({ field, prev, next }) => (
              <div className="patch-field" key={field}>
                <div className="patch-field-name">{field}</div>
                <div className="patch-old">{fmt(next)}</div>
                <div className="patch-new">{fmt(prev)}</div>
              </div>
            ))
          )}
          {selected.skills.length > 0 && (
            <>
              <div className="panel-head">Skills frozen at publish</div>
              <div className="muted" style={{ fontSize: 12.5 }}>
                {selected.skills.map((s) => s.name || s.id).join(", ")}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
